import axios from 'axios';
import Qs from 'qs';
import orderBus from '@/js/helpers/orderBus';
import errors from '@/js/helpers/apiErrors';
import isLocalhost from '@/js/utils/isLocalhost';
import cloneDeep from 'lodash/cloneDeep';

export default new class apiRequest {
  constructor() {
    const $html = document.querySelector('html');

    this.sessid = $html.dataset.sessid;
    this.token = $html.dataset.token;
    this.typePlatform = $html.dataset.typePlatform;

    this.service = axios.create(apiRequest.getConfig());

    if (this.token) {
      this.service.defaults.headers['X-TOKEN'] = this.token;

      if (isLocalhost) {
        this.service.defaults.baseURL = 'http://demo.book24.ru/api/v1/';
      }
    }
  }

  /**
   * Возвращает объект конфигурации
   * @returns {Object} Объект конфигурации
   */
  static getConfig() {
    return {
      baseURL: '/api/v1',
    };
  }

  /**
   * Возвращает параметры для url
   * @returns {Object} Объект параметров
   */
  getParamsUrl(typePlatform, sessid) {
    const params = {
      TYPE_PLATFORM: typeof typePlatform === 'string' ? typePlatform : this.typePlatform,
    };

    if (process.env.NODE_ENV === 'production' || process.env.WATCH) {
      Object.assign(params, {
        SESSID: typeof sessid === 'string' ? sessid : this.sessid,
      });
    }

    return params;
  }

  /**
   * Обработчик ошибок
   * @param reject
   * @param error
   * @param args
   */
  static onFail(reject, error, ...args) {
    try {
      const { CODE: code, MESSAGE: message } = error.response.data;

      // обработка ошибки пустой корзины
      if (code === errors.emptyCart.code) {
        window.location.href = '/personal/cart';
      }

      // выбросить событие ошибки для обработки в заказе
      orderBus.$emit('api-request-error', code, message);

      reject(error.response, ...args);
    } catch (e) {
      reject(e);
    }
  }

  /**
   * Возвращает ответ GET запроса
   * @param {String} path - Url
   * @param {Object} [payload={}] - Объект параметров
   * @param {String} platform - Тип платформы
   * @param {String} sessid - ID сессии
   * @param {Object|null} [headers={}] - Объект с заголовками
   * @returns {Promise<unknown>} Обещание вернуть объект после запроса
   */
  async get(path, payload = {}, platform = null, sessid = null, headers = null) {
    return new Promise((resolve, reject) => {
      this.service.request({
        method: 'GET',
        url: path,
        params: Object.assign(payload, this.getParamsUrl(platform, sessid)),
        headers: this.mergeHeaders(headers),
        paramsSerializer(params) {
          return Qs.stringify(params);
        },
      })
        .then(response => resolve(response.data))
        .catch((...arg) => apiRequest.onFail(reject, ...arg));
    });
  }

  /**
   * Возвращает ответ POST запроса c formData
   * @param {String} path - Url
   * @param {Object} [payload={}] - Объект параметров
   * @param {String} platform - Тип платформы
   * @param {String} sessid - ID сессии
   * @param {Object|null} [headers={}] - Объект с заголовками
   * @param {Boolean} [isFormData] - Обрабатывать payload как FormData
   * @returns {Promise<unknown>} Обещание вернуть объект после запроса
   */
  async post(path,
    payload = {},
    platform = null,
    sessid = null,
    headers = null,
    isFormData = true) {
    return new Promise((resolve, reject) => {
      const request = {
        method: 'POST',
        url: path,
        params: this.getParamsUrl(platform, sessid),
        headers: this.mergeHeaders(headers),
        data: payload,
      };

      if (isFormData) {
        request.transformRequest = data => Qs.stringify(data);
        request.paramsSerializer = params => Qs.stringify(params);
      }

      this.service.request(request)
        .then(response => resolve(response.data))
        .catch((...arg) => apiRequest.onFail(reject, ...arg));
    });
  }

  /**
   * Возвращает ответ POST запроса c queryParams
   * @param {String} path - Url
   * @param {Object} [payload={}] - Объект параметров
   * @param {String} platform - Тип платформы
   * @param {String} sessid - ID сессии
   * @param {Object|null} [headers={}] - Объект с заголовками
   * @returns {Promise<unknown>} Обещание вернуть объект после запроса
   */
  async postQueryParams(path, payload = {}, platform = null, sessid = null, headers = null) {
    return new Promise((resolve, reject) => {
      this.service.request({
        method: 'POST',
        url: path,
        params: Object.assign(payload, this.getParamsUrl(platform, sessid)),
        headers: this.mergeHeaders(headers),
        paramsSerializer(params) {
          return Qs.stringify(params);
        },
      })
        .then(response => resolve(response.data))
        .catch((...arg) => apiRequest.onFail(reject, ...arg));
    });
  }

  /**
   * Возвращает ответ DELETE запроса
   * @param {String} path - Url
   * @param {Object} [payload={}] - Объект параметров
   * @param {String} platform - Тип платформы
   * @param {String} sessid - ID сессии
   * @param {Object|null} [headers={}] - Объект с заголовками
   * @returns {Promise<unknown>} Обещание вернуть объект после запроса
   */
  async delete(path, payload = {}, platform = null, sessid = null, headers = null) {
    return new Promise((resolve, reject) => {
      this.service.request({
        method: 'DELETE',
        url: path,
        params: this.getParamsUrl(platform, sessid),
        headers: this.mergeHeaders(headers),
        data: payload,
        paramsSerializer(params) {
          return Qs.stringify(params);
        },
      })
        .then(response => resolve(response.data))
        .catch((...arg) => apiRequest.onFail(reject, ...arg));
    });
  }

  /**
   * Возвращает ответ PUT запроса c formData
   * @param {String} path - Url
   * @param {Object} [payload={}] - Объект параметров
   * @param {String} platform - Тип платформы
   * @param {String} sessid - ID сессии
   * @param {Object|null} [headers={}] - Объект с заголовками
   * @param {Boolean} [isFormData] - Обрабатывать payload как FormData
   * @returns {Promise<unknown>} Обещание вернуть объект после запроса
   */
  async put(path,
    payload = {},
    platform = null,
    sessid = null,
    headers = null,
    isFormData = true) {
    return new Promise((resolve, reject) => {
      const request = {
        method: 'PUT',
        url: path,
        params: this.getParamsUrl(platform, sessid),
        headers: this.mergeHeaders(headers),
        data: payload,
      };

      if (isFormData) {
        request.transformRequest = data => Qs.stringify(data);
        request.paramsSerializer = params => Qs.stringify(params);
      }

      this.service.request(request)
        .then(response => resolve(response.data))
        .catch((...arg) => apiRequest.onFail(reject, ...arg));
    });
  }

  /**
   * Возвращает ответ PATCH запроса
   * @param {String} path - Url
   * @param {Object} [payload={}] - Объект параметров
   * @param {String} platform - Тип платформы
   * @param {String} sessid - ID сессии
   * @param {Object|null} [headers={}] - Объект с заголовками
   * @returns {Promise<unknown>} Обещание вернуть объект после запроса
   */
  async patch(path, payload = {}, platform = null, sessid = null, headers = null) {
    return new Promise((resolve, reject) => {
      this.service.request({
        method: 'PATCH',
        url: path,
        params: this.getParamsUrl(platform, sessid),
        headers: this.mergeHeaders(headers),
        data: payload,
        transformRequest(data) {
          return Qs.stringify(data);
        },
        paramsSerializer(params) {
          return Qs.stringify(params);
        },
      })
        .then(response => resolve(response.data))
        .catch((...arg) => apiRequest.onFail(reject, ...arg));
    });
  }

  /**
   * Загружает бинарный файл
   * @param {String} path - Url
   * @param {File} file - Файл
   * @param {String} platform - Тип платформы
   * @param {String} sessid - ID сессии
   * @returns {Promise<unknown>}
   */
  async upload(path, file, platform = null, sessid = null) {
    const formData = new FormData();
    formData.append('file', file);

    return new Promise((resolve, reject) => {
      this.service.request({
        method: 'POST',
        url: path,
        params: this.getParamsUrl(platform, sessid),
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        data: formData,
      })
        .then(response => resolve(response.data))
        .catch((...arg) => apiRequest.onFail(reject, ...arg));
    });
  }

  /**
   * Скачивает бинарный файл
   * @param {String} path - Url
   * @param {String} platform - Тип платформы
   * @param {String} sessid - ID сессии
   * @returns {Promise<unknown>}
   */
  async download(path, platform = null, sessid = null) {
    return new Promise((resolve, reject) => {
      this.service.request({
        method: 'GET',
        url: path,
        params: this.getParamsUrl(platform, sessid),
        responseType: 'blob',
      })
        .then(response => resolve(response.data))
        .catch((...arg) => apiRequest.onFail(reject, ...arg));
    });
  }

  /**
   * Объединяет между собой текущие заголовки axios и новые заголовки, указанные разработчиком
   * Значения заголовков по умолчанию (из axios) заменятся на новые (из объекта {newHeaders})
   * @param {Object} newHeaders Новые заголовки, в формате ключ -> значение
   * @returns {*}
   */
  mergeHeaders(newHeaders) {
    const defaultClone = cloneDeep(this.service.defaults.headers || {});
    const newClone = cloneDeep(newHeaders || {});

    return Object.assign(defaultClone, newClone);
  }
}();
